// ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
//  t548 (29-oct-2026) · «ESTA PESTAÑA ES DE OTRA CLÍNICA» — la pestaña que se ha quedado atrás lo dice y ofrece recargar
// ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
//
//  Cuando otra pestaña de este navegador entra en otra clínica (el soporte de Biango con «Abrir su clínica») o cambia de
//  cuenta o de clínica («Cambiar de clínica» de quien trabaja en varias), ESTA sigue enseñando lo de la de antes. Desde
//  t548 ya no manda nada (services/api lo para, y el servidor también: middleware/auth, 409 `pestana_de_otra_clinica`); esto
//  es lo que se lo cuenta a quien la mira, para que no crea que lo que escribe se está guardando.
//
//  SE ENTERA SIN PREGUNTAR AL SERVIDOR: con el aviso del navegador (el evento `storage`, cuando la otra pestaña toca la
//  sesión), con los avisos de la visita (services/api · suscribirVisita) y con el de una petición parada (el evento
//  `biango:pestana-de-otra-clinica`). La verdad la da `pestanaDeOtraClinica()`, que mira la clínica que pinta esta pestaña
//  contra la del navegador: un mensaje que se pierda solo retrasa el aviso hasta el siguiente intento.
//
//  NO SE CIERRA Y NO RECARGA SOLO: lo que haya a medio escribir es de quien lo escribe (puede copiarlo antes). La salida es
//  «Recargar esta pestaña»: vuelve a arrancar con la clínica de ahora y sin nada de la de antes.
//  Montado una vez en App, junto a CuentasGlobales. Encima de las ventanas, como los demás avisos del sistema
//  (backend/routes/capas-menu-encima-de-los-avisos.test.js).
import { useEffect, useState } from 'react'
import { pestanaDeOtraClinica, suscribirVisita } from '../services/api'
import { t } from '../utils/i18n'

// Los portales (el del paciente, el del laboratorio, el del anestesista) tienen su propia sesión: lo de la del equipo no va
// con ellos.
function enUnPortal() {
  try { return /^\/(portal|lab|anestesista)(\/|\?|$)/.test(String(window.location.pathname || '')) } catch (_) { return false }
}

export default function PestanaDeOtraClinica() {
  const [otra, setOtra] = useState(() => pestanaDeOtraClinica())

  useEffect(() => {
    const mirar = () => { setOtra(pestanaDeOtraClinica()) }
    const quitarVisita = suscribirVisita(mirar)
    const quitar = []
    if (typeof window !== 'undefined' && window.addEventListener) {
      for (const ev of ['storage', 'biango:pestana-de-otra-clinica', 'focus']) {
        window.addEventListener(ev, mirar)
        quitar.push(() => window.removeEventListener(ev, mirar))
      }
    }
    mirar()
    return () => { quitarVisita(); for (const q of quitar) q() }
  }, [])

  if (!otra || enUnPortal()) return null
  const recargar = () => { try { window.location.reload() } catch (_) { /* sin ventana */ } }
  const donde = otra.nombre
    ? t('components.PestanaDeOtraClinica.donde_visita', 'En otra pestaña de este navegador se ha entrado en {{nombre}}.', { nombre: String(otra.nombre).toUpperCase() })
    : t('components.PestanaDeOtraClinica.donde', 'En otra pestaña de este navegador se ha entrado en otra clínica o con otra cuenta.')
  return (
    <div role="alert" data-pestana-de-otra-clinica="" style={{ position: 'fixed', left: '16px', right: '16px', top: 'calc(12px + var(--sa-arriba))', zIndex: 99985, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
      <div style={{ pointerEvents: 'auto', maxWidth: '560px', width: '100%', background: 'var(--surface)', color: 'var(--text-primary)',
        border: '2px solid var(--danger)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: '14px 16px',
        display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <strong style={{ fontSize: '15px', color: 'var(--danger-tinta, var(--danger))' }}>
          {t('components.PestanaDeOtraClinica.titulo', 'Esta pestaña es de otra clínica')}
        </strong>
        <span style={{ fontSize: '13px', lineHeight: 1.45 }}>
          {donde}{' '}
          {t('components.PestanaDeOtraClinica.que_pasa', 'Lo que ves aquí es de la de antes: para no guardar nada en la clínica equivocada, esta pestaña ya no envía nada. Si tenías algo a medio escribir, cópialo antes de recargar.')}
        </span>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={recargar}
            style={{ minHeight: '40px', padding: '8px 16px', borderRadius: 'var(--radius)', border: '1px solid var(--danger)',
              background: 'var(--danger)', color: '#fff', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
            {t('components.PestanaDeOtraClinica.recargar', 'Recargar esta pestaña')}
          </button>
        </div>
      </div>
    </div>
  )
}
